/**
 * Pure group math for NxTable's row grouping (`groupBy`, I-2032).
 *
 * Grouping is a stable partition: rows are sorted into their group's block and
 * keep the relative order they have in `data`, so whatever sort or filter the
 * consumer applied to `data` holds inside every group. The result is a
 * permutation of `data`'s indices (`order`, display position → data index)
 * plus the groups as sections of it. These helpers never touch the DOM or Vue
 * reactivity, so they are unit-testable.
 */
import { UNGROUPED_GROUP_KEY, type NxTableGroup } from '../internal';
import type { RowBlock } from './rowTree';

/** One group as a contiguous block of the display order. */
export type RowGroupSection = {
  group: NxTableGroup;
  /** True for the implicit group of rows without a listed key. */
  ungrouped: boolean;
  /**
   * Display position of the group's first row. An empty group (only with
   * `showEmptyGroups`) carries the position its rows would start at.
   */
  start: number;
  /** Number of rows in the group. */
  size: number;
};

export type RowGroupLayout = {
  /** Display position → index in `data`; a permutation of `data`'s indices. */
  order: number[];
  /** The shown groups in display order. Placeholder rows follow the last one, outside any group. */
  sections: RowGroupSection[];
};

export type RowGroupOptions<T> = {
  groupBy: (row: T) => string | null | undefined;
  /**
   * The groups in display order. `undefined` forms one group per distinct key,
   * in order of first appearance, titled with the key.
   */
  groups?: readonly NxTableGroup[];
  /** Title of the implicit group (key missing or not listed). */
  ungroupedLabel: string;
  /** Keep listed groups without rows as (empty) sections. */
  showEmptyGroups?: boolean;
  /** Windowed/sparse shells have no key yet; they follow all groups. */
  isPlaceholderRow?: (row: T) => boolean;
  /**
   * Blocks that must stay together, in data order and covering every row — the
   * subtrees of a tree (`rowParentId`). The block's first row decides its group.
   * Default: every row is a block of its own.
   */
  units?: readonly RowBlock[];
  warn?: (message: string) => void;
};

type Bucket = {
  group: NxTableGroup;
  ungrouped: boolean;
  /** Listed in `groups` — only those are kept while empty. */
  listed: boolean;
  indices: number[];
};

/** Sort the rows of `rows` into their groups (see the file comment). */
export function partitionRowGroups<T>(
  rows: readonly T[],
  options: RowGroupOptions<T>
): RowGroupLayout {
  const buckets: Bucket[] = [];
  const byKey = new Map<string, Bucket>();
  const listed = options.groups !== undefined;
  for (const group of options.groups ?? []) {
    if (byKey.has(group.key)) {
      options.warn?.(
        `NxTable groups: the key "${group.key}" is listed twice; the first entry wins.`
      );
      continue;
    }
    const bucket: Bucket = { group, ungrouped: false, listed: true, indices: [] };
    buckets.push(bucket);
    byKey.set(group.key, bucket);
  }
  const ungrouped: Bucket = {
    group: { key: UNGROUPED_GROUP_KEY, label: options.ungroupedLabel },
    ungrouped: true,
    listed: false,
    indices: [],
  };
  const placeholders: number[] = [];

  function bucketOf(row: T): Bucket {
    const raw = options.groupBy(row);
    if (raw === null || raw === undefined) return ungrouped;
    // Tolerate a number from untyped callers: `groups` keys are strings.
    const key = String(raw);
    const known = byKey.get(key);
    if (known) return known;
    if (listed || key === UNGROUPED_GROUP_KEY) return ungrouped;
    const bucket: Bucket = {
      group: { key, label: key },
      ungrouped: false,
      listed: false,
      indices: [],
    };
    buckets.push(bucket);
    byKey.set(key, bucket);
    return bucket;
  }

  function place(start: number, size: number): void {
    const first = rows[start];
    const target =
      first === undefined || options.isPlaceholderRow?.(first)
        ? placeholders
        : bucketOf(first).indices;
    for (let i = start; i < start + size && i < rows.length; i++) target.push(i);
  }

  if (options.units) {
    for (const unit of options.units) place(unit.start, unit.size);
  } else {
    for (let i = 0; i < rows.length; i++) place(i, 1);
  }

  const order: number[] = [];
  const sections: RowGroupSection[] = [];
  for (const bucket of [...buckets, ungrouped]) {
    const size = bucket.indices.length;
    if (size === 0 && !(options.showEmptyGroups && bucket.listed)) continue;
    sections.push({ group: bucket.group, ungrouped: bucket.ungrouped, start: order.length, size });
    for (const index of bucket.indices) order.push(index);
  }
  for (const index of placeholders) order.push(index);
  return { order, sections };
}

/** One header row as the table renders it. */
export type RowGroupHeader<T> = {
  /** The group key — unique per table, so it keys the header for Vue and the virtualizer. */
  key: string;
  group: NxTableGroup;
  ungrouped: boolean;
  count: number;
  /** The group's rows in display order. */
  rows: T[];
  /**
   * Display position of the row the header sits directly above; the row count
   * for a header behind the last row.
   */
  anchor: number;
  /** 0-based position among ALL body rows, headers included (for `aria-rowindex`). */
  position: number;
};

export type RowGroupRender<T> = {
  /** Headers rendered directly above the row at a display position. */
  byAnchor: Map<number, RowGroupHeader<T>[]>;
  /** Headers of empty groups behind the last row. */
  trailing: RowGroupHeader<T>[];
  /** Every header in display order. */
  headers: RowGroupHeader<T>[];
  /** Per display position: the position its group starts at (zebra restarts per group). */
  sectionStart: Int32Array;
};

/** Turn a layout into the header rows around the display order of `rows`. */
export function renderRowGroups<T>(rows: readonly T[], layout: RowGroupLayout): RowGroupRender<T> {
  const count = layout.order.length;
  const byAnchor = new Map<number, RowGroupHeader<T>[]>();
  const trailing: RowGroupHeader<T>[] = [];
  const headers: RowGroupHeader<T>[] = [];
  const sectionStart = new Int32Array(count);

  let covered = 0;
  layout.sections.forEach((section, ordinal) => {
    const groupRows: T[] = [];
    for (let i = section.start; i < section.start + section.size; i++) {
      groupRows.push(rows[layout.order[i]]);
      sectionStart[i] = section.start;
    }
    covered = Math.max(covered, section.start + section.size);
    const header: RowGroupHeader<T> = {
      key: section.group.key,
      group: section.group,
      ungrouped: section.ungrouped,
      count: section.size,
      rows: groupRows,
      anchor: section.start,
      // Every earlier header sits above this one; so do the `start` rows.
      position: section.start + ordinal,
    };
    headers.push(header);
    if (section.start >= count) {
      trailing.push(header);
    } else {
      const atAnchor = byAnchor.get(section.start);
      if (atAnchor) atAnchor.push(header);
      else byAnchor.set(section.start, [header]);
    }
  });
  // Placeholder rows follow the groups as one block of their own.
  for (let i = covered; i < count; i++) sectionStart[i] = covered;

  return { byAnchor, trailing, headers, sectionStart };
}

/** Same groups, same order: re-rendering would change nothing. */
export function sameRowGroupLayout(a: RowGroupLayout, b: RowGroupLayout): boolean {
  if (a.order.length !== b.order.length || a.sections.length !== b.sections.length) return false;
  for (let i = 0; i < a.order.length; i++) if (a.order[i] !== b.order[i]) return false;
  return a.sections.every((section, i) => {
    const other = b.sections[i];
    return (
      section.start === other.start &&
      section.size === other.size &&
      section.ungrouped === other.ungrouped &&
      sameGroup(section.group, other.group)
    );
  });
}

export function sameGroup(a: NxTableGroup, b: NxTableGroup): boolean {
  return a.key === b.key && a.label === b.label && a.tone === b.tone && a.hue === b.hue;
}
